import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, Input, toast, useReturnOrigin, withReturn } from '../../../components/ui';
import { ApiError } from '../../../lib/api';
import { formatHours, formatMoney } from '../../../lib/format';
import { useAgreementPrepaid } from '../../agreements/PrepaidHoursCard';
import { useSellSupportHours } from '../../support/api';
import { todayIso } from '../line-editor';
import { ClientAgreementFields, useClientAgreements, useInvoiceTarget } from './ClientAgreementFields';

/** "Support Agreement - 09/2026" — the name Harvest's renewals already use. */
function supportAgreementName(): string {
  const [y, m] = todayIso().split('-');
  return `Support Agreement - ${m}/${y}`;
}

type Field = 'client' | 'agreement' | 'hours' | 'rate' | 'expires' | null;

/**
 * PREPAID HOURS — the invoice this business raises most (Jesse, 2026-09-30:
 * *"most of our clients pay for hours up front and then we use that bank of
 * hours to burn down… select a client (or new client), select an existing
 * agreement or a new agreement, and then the number of hours being
 * purchased"*).
 *
 * It is the agreement page's "Sell hours", reachable without going to the
 * agreement first: the same `POST /support/banks/blocks/sell`, so the draft
 * invoice and the PENDING block behind it are created together and the hours
 * land in the bank — on THIS agreement — the moment the invoice is sent. An
 * invoice built by hand on the Custom tab creates no block, which is exactly
 * how prepaid hours stopped connecting to the agreements they were bought for.
 */
export function PrepaidHoursTab() {
  const navigate = useNavigate();
  const from = useReturnOrigin();
  const sell = useSellSupportHours();
  const { clientId, agreementId } = useInvoiceTarget();
  const agreements = useClientAgreements(clientId);
  const agreement = agreements.data?.agreements.find((a) => a.id === agreementId) ?? null;
  const prepaid = useAgreementPrepaid(agreementId, !!agreement);

  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<{ field: Field; message: string } | null>(null);

  // The rate is the agreement's — re-seeded whenever the agreement changes,
  // and editable for this sale only.
  const seededFor = useRef('');
  useEffect(() => {
    if (!agreement || seededFor.current === agreement.id) return;
    seededFor.current = agreement.id;
    setRate(agreement.hourlyRate !== null ? agreement.hourlyRate.toFixed(2) : '');
  }, [agreement]);

  const hoursNum = Number(hours);
  const rateNum = Number(rate);
  const total =
    Number.isFinite(hoursNum) && Number.isFinite(rateNum) && hoursNum > 0 && rateNum > 0 ? hoursNum * rateNum : null;

  const fail = (field: Field, message: string) => {
    setError({ field, message });
    const el = document.querySelector<HTMLElement>(`[data-draft-field="${field}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.querySelector<HTMLElement>('select, input')?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) return fail('client', 'Choose the client buying the hours.');
    if (!agreementId) return fail('agreement', 'Choose the agreement the hours are for — or create one.');
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) return fail('hours', 'Enter how many hours they are buying.');
    if (!Number.isFinite(rateNum) || rateNum <= 0) {
      return fail('rate', 'Enter the rate per hour — this agreement has none set.');
    }
    if (expiresAt && expiresAt < todayIso()) return fail('expires', 'The expiry date must be today or later.');
    setError(null);
    try {
      const result = await sell.mutateAsync({
        clientId,
        agreementId,
        hours: hoursNum,
        rate: rateNum,
        issueDate,
        expiresAt: expiresAt || null,
      });
      toast.success(
        `Draft #${result.invoiceNumber} created — ${formatHours(result.hours, { unit: true })} at ${formatMoney(result.rate)}/h. The hours go into the bank when it is sent.`,
      );
      navigate(withReturn(`/invoices/${result.invoiceId}`, from));
    } catch (err) {
      setError({ field: null, message: err instanceof ApiError ? err.message : 'Something went wrong — try again.' });
    }
  };

  const fieldError = (f: Field) => (error?.field === f ? error.message : undefined);

  const bank = prepaid.data?.applies ? prepaid.data : null;
  const agreementHint = agreement ? (
    <span className="num">
      {agreement.hourlyRate !== null ? `${formatMoney(agreement.hourlyRate)}/h` : 'No hourly rate set'}
      {bank && (bank.purchased > 0 || bank.pending > 0) && (
        <>
          {' · '}
          {formatHours(bank.purchased, { unit: true })} bought,{' '}
          {formatHours(bank.burnt, { unit: true })} used,{' '}
          <span className={bank.available < 0 ? 'font-semibold text-status-late' : 'font-semibold text-secondary'}>
            {bank.available < 0
              ? `${formatHours(-bank.available, { unit: true })} over`
              : `${formatHours(bank.available, { unit: true })} left`}
          </span>
          {bank.pending > 0 && ` · ${formatHours(bank.pending, { unit: true })} on draft invoices, not sent yet`}
        </>
      )}
    </span>
  ) : null;

  return (
    <Card>
      <CardHeader
        title="Prepaid hours"
        subtitle="A draft invoice for hours bought up front. They go into the agreement's bank when it is sent."
      />
      <form onSubmit={submit} noValidate>
        <CardBody className="space-y-5">
          <ClientAgreementFields
            preferTypes={['SUPPORT']}
            newAgreementDefaults={() => ({
              type: 'SUPPORT',
              billBy: 'HOURS',
              name: supportAgreementName(),
              ...(rate ? { hourlyRate: rate } : {}),
            })}
            errors={{ client: fieldError('client'), agreement: fieldError('agreement') }}
            onCleared={() => setError(null)}
            agreementHint={agreementHint}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div data-draft-field="hours">
              <Input
                label="Hours purchased"
                type="number"
                inputMode="decimal"
                min="0.25"
                step="0.25"
                placeholder="e.g. 20"
                value={hours}
                error={fieldError('hours')}
                onChange={(e) => {
                  setHours(e.target.value);
                  if (error?.field === 'hours') setError(null);
                }}
              />
            </div>
            <div data-draft-field="rate">
              <Input
                label="Rate per hour ($)"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={rate}
                error={fieldError('rate')}
                hint={agreement && agreement.hourlyRate === null ? 'The agreement has no rate — enter the client’s.' : undefined}
                onChange={(e) => {
                  setRate(e.target.value);
                  if (error?.field === 'rate') setError(null);
                }}
              />
            </div>
            <Input
              label="Invoice date"
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value || todayIso())}
              hint="Due on receipt."
            />
            <div data-draft-field="expires">
              <Input
                label="Hours expire on"
                type="date"
                value={expiresAt}
                min={todayIso()}
                error={fieldError('expires')}
                onChange={(e) => {
                  setExpiresAt(e.target.value);
                  if (error?.field === 'expires') setError(null);
                }}
                hint="Optional — blank never expires."
              />
            </div>
          </div>
        </CardBody>
        <CardBody className="border-t border-grey-200 pt-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {error && error.field === null && (
              <p role="alert" className="mr-auto flex items-center gap-1.5 text-body-sm font-semibold text-error">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                {error.message}
              </p>
            )}
            <span className="num text-body-sm text-secondary">
              {total !== null ? (
                <>
                  {formatHours(hoursNum, { unit: true })} × {formatMoney(rateNum)} ={' '}
                  <strong>{formatMoney(total)}</strong>
                </>
              ) : (
                <span className="text-grey-400">Total —</span>
              )}
            </span>
            <Button type="submit" size="lg" loading={sell.isPending}>
              Create draft invoice
            </Button>
          </div>
        </CardBody>
      </form>
    </Card>
  );
}
